import * as React from 'react';
import { useMemo, useCallback } from 'react';

import {
  AuthContext,
  AuthContextState,
  Authenticated,
  AuthenticationError,
  AuthenticationState,
  DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE,
  LoginCredentials,
  LoginResult,
} from './AuthContext';
import { getDomain, LOGOUT_URL } from './globals';
import { UserSessionQuery, UserSessionQueryVariables } from './__generated__/types';
import { GraphQLClient, useGraphqlQuery } from './graphql';

export type TrackError = (
  label: string,
  error?: Error,
  data?: {
    [k: string]: unknown;
  },
) => void;
export type AuthContextProviderProps = {
  env: string;
  client: GraphQLClient;
  fetch?: typeof fetch;
  trackError?: TrackError;
};

/** TODO: Add documentation */
export const AuthContextProvider: React.FC<AuthContextProviderProps> = ({
  trackError = () => {},
  client,
  fetch: fetchFn = fetch,
  env,
  children,
}) => {
  // TODO: Check if we have cookie so we can possibly skip the network call.
  const queryResult = useGraphqlQuery<UserSessionQuery, UserSessionQueryVariables>(
    client,
    userSessionQuery,
    {},
  );

  const logout: AuthContextState['logout'] = useCallback(() => {
    return fetchFn(`${getDomain(env)}/session/sign-out`, { method: 'GET', credentials: 'include' })
      .then(() => {
        window.location.reload();
      })
      .catch((e) => {
        trackError('logout', e);
        window.location.assign(LOGOUT_URL(env));
      });
  }, [env, fetchFn, trackError]);

  const verifyLogin: AuthContextState['verifyLogin'] = useCallback(
    (credentials) => callVerifyLogin(env, fetchFn, trackError, credentials),
    [env, fetchFn, trackError],
  );

  const authentication: AuthenticationState = useMemo(() => {
    if (queryResult.loading) {
      return {
        state: 'authenticating',
        user: undefined,
      };
    }
    if (queryResult.error) {
      trackError('UserSession error', queryResult.error);
      return {
        state: 'error',
        user: undefined,
        message: queryResult.error.message,
      };
    }
    const userSession = queryResult.data?.web.userSession;
    if (!userSession) {
      // If UserSession is missing, the server returned invalid data.
      const message = 'invalid data returned: missing UserSession';
      trackError(message);
      return {
        state: 'error',
        user: undefined,
        message,
      };
    }

    if (!userSession.human) {
      return {
        state: 'not_authenticated',
        user: null,
      };
    }

    return convertHumanToUser(userSession.human, trackError);
    // trackError isn't needed here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryResult.loading, queryResult.data, queryResult.error, logout]);

  return (
    <AuthContext.Provider
      value={{ authentication, logout, refresh: queryResult.refresh, verifyLogin }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function convertHumanToUser(
  human: Exclude<
    Exclude<UserSessionQuery['web']['userSession'], null | undefined>['human'],
    null | undefined
  >,
  trackError: TrackError,
): Authenticated | AuthenticationError {
  // TODO: Remove this check (see feature request here: https://github.com/opendoor-labs/code/pull/35285)
  if (!human.email) {
    const message = 'invalid data returned: missing Human.email';
    trackError(message);
    return {
      state: 'error',
      user: undefined,
      message,
    };
  }

  const fullName = human.full_name || human.first_name || 'Customer';
  const firstName = human.first_name || human.full_name || 'Customer';

  const hasOpendoorEmail = human.email.endsWith('@opendoor.com');
  const hasOpendoorInternalAccess = human.opendoor_internal_access || false;

  return {
    state: 'authenticated',
    user: {
      humanToken: human.token,
      customerUuid: human.customer_uuid || null,
      email: human.email,
      fullName,
      firstName,
      hasOpendoorEmail,
      hasOpendoorInternalAccess,
    },
  };
}

async function callVerifyLogin(
  env: string,
  fetchFn: typeof fetch,
  trackError: TrackError,
  credentials: LoginCredentials,
): Promise<LoginResult> {
  let payload: unknown;
  if ('googleIdToken' in credentials) {
    payload = { google_id_token: credentials.googleIdToken };
  } else if ('facebookIdToken' in credentials) {
    payload = { facebook_id_token: credentials.facebookIdToken };
  } else {
    payload = {
      human: {
        email: credentials.email,
        password: credentials.password,
      },
    };
  }

  // See `verify_login` at
  // https://github.com/opendoor-labs/web/blob/master/app/controllers/humans/sessions_controller.rb
  let resp;
  try {
    resp = await fetchFn(`${getDomain(env)}/verify_login`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  } catch (e) {
    trackError('login', e as Error);
    return { success: false, displayErrorMessage: DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE };
  }

  if (resp.ok) {
    return { success: true };
  } else {
    let displayErrorMessage = DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE;
    try {
      const json = await resp.json();
      if ('error' in json) {
        displayErrorMessage = json['error'];
      }
    } catch (e) {
      trackError('login error without error message', e as Error);
    }
    return {
      success: false,
      displayErrorMessage,
    };
  }
}

export const userSessionQuery = /* GraphQL */ `
  query UserSession {
    web {
      userSession {
        human {
          token
          customer_uuid
          email
          first_name
          full_name
          opendoor_internal_access
        }
      }
    }
  }
`;
